import React from "react";
import { ImageIcon } from "lucide-react";

/**
 * Captures de l'application BoostPro.
 *
 * 👉 POUR REMPLACER LES PLACEHOLDERS PAR LES VRAIES CAPTURES :
 *    Modifier simplement le tableau `screenshots` ci-dessous en passant
 *    une URL d'image dans le champ `src` (PNG/JPG/WEBP).
 *    Tant que `src` est vide (chaîne vide), un placeholder stylisé s'affiche.
 *
 *    Exemple :
 *      { label: "Tableau de bord", src: "/screenshots/dashboard.png" }
 *
 *    Format recommandé : portrait, ratio ~9:19 (capture de téléphone),
 *    largeur 600-1080 px, fond sombre pour rester cohérent avec le thème.
 */
const screenshots = [
  { label: "Tableau de bord", caption: "Une vue d'ensemble en un coup d'œil.", src: "" },
  { label: "Clients", caption: "Toutes les fiches clients à portée de main.", src: "" },
  { label: "Facturation", caption: "Créez une facture en moins d'une minute.", src: "" },
  { label: "Dépenses", caption: "Suivez vos achats professionnels.", src: "" },
  { label: "Véhicule", caption: "Carburant, entretien et kilométrages.", src: "" },
  { label: "Statistiques", caption: "Analysez votre rentabilité.", src: "" },
];

const Placeholder = ({ label }) => (
  <div className="relative h-full w-full flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-[#0b1b34] to-slate-950 text-slate-400">
    <div className="absolute inset-0 bg-grid opacity-20" />
    <div className="relative h-14 w-14 rounded-2xl bg-blue-500/10 ring-1 ring-blue-400/20 flex items-center justify-center">
      <ImageIcon size={22} className="text-blue-300" />
    </div>
    <div className="relative mt-4 text-xs uppercase tracking-[0.2em] text-blue-300/80 font-semibold">Aperçu</div>
    <div className="relative mt-1 text-sm text-slate-300 font-medium px-4 text-center">{label}</div>
    <div className="relative mt-2 text-[10px] text-slate-500">Capture à venir</div>
  </div>
);

export default function Screenshots() {
  return (
    <section data-testid="section-screenshots" className="relative py-24 md:py-28 overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-px divider-line" />
      <div className="absolute -top-20 right-0 h-[360px] w-[360px] rounded-full bg-emerald-500/10 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-[0.22em] text-emerald-400">Aperçus</span>
          <h2 className="mt-3 font-display text-3xl md:text-5xl font-semibold text-white tracking-tight">
            Captures de <span className="text-gradient-blue">l'application.</span>
          </h2>
          <p className="mt-4 text-slate-400">
            Un aperçu de l'expérience BoostPro sur mobile. Les visuels définitifs seront
            ajoutés au lancement de l'application.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
          {screenshots.map((s, i) => (
            <figure
              key={s.label}
              data-testid={`screenshot-${i}`}
              className="group rounded-3xl p-2 bg-gradient-to-b from-slate-800 to-slate-950 border border-white/5 hover:border-blue-400/30 transition-all duration-500 shadow-[0_20px_60px_-20px_rgba(59,130,246,0.25)]"
            >
              <div className="relative aspect-[9/19] rounded-2xl overflow-hidden border border-white/5 bg-[#060B14]">
                {s.src ? (
                  <img
                    src={s.src}
                    alt={`Capture BoostPro — ${s.label}`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                  />
                ) : (
                  <Placeholder label={s.label} />
                )}
              </div>
              <figcaption className="px-2 pt-3 pb-2">
                <div className="text-sm font-semibold text-white">{s.label}</div>
                <div className="text-xs text-slate-400 leading-snug">{s.caption}</div>
              </figcaption>
            </figure>
          ))}
        </div>

        <p className="mt-10 text-center text-xs text-slate-500">
          Les captures réelles seront ajoutées prochainement.
        </p>
      </div>
    </section>
  );
}
